<template>
  <div class="sc-message">
    <div class="sc-message--content" :class="{
        system: message.type === 'system',
        sent: message.direction === 'sent' && message.type !== 'system',
        received: message.direction === 'received' && message.type !== 'system',
        carousel: isCarouselMessage
      }"
      :style="{width: isCarouselMessage ? '' : colors.messageBubble.width}"
    >
      <slot
        v-if="message.direction === 'received' && chatImageUrl"
        name="user-avatar"
        :message="message"
        :user="user">
          <div v-if="message.type !== 'system'" :title="authorName" class="sc-message--avatar" :style="{
            backgroundImage: `url(${chatImageUrl})`
          }"></div>
      </slot>

      <TextMessage
        :class="messageClass"
        v-if="message.type === 'text'"
        :message="message"
        :messageColors="determineMessageColors()"
        :messageStyling="messageStyling"
        @remove="$emit('remove')"
      >
        <template v-slot:default="scopedProps">
          <slot name="text-message-body" :message="scopedProps.message" :messageText="scopedProps.messageText"
                :messageColors="scopedProps.messageColors" :me="scopedProps.me">
          </slot>
        </template>

        <template v-slot:text-message-toolbox="scopedProps">
          <slot name="text-message-toolbox" :message="scopedProps.message" :me="scopedProps.me">
          </slot>
        </template>

        <template #label v-if="bubbleMetadata.type === 'validation'">
          <p class="bubble-label">{{ bubbleMetadata.field }}</p>
        </template>
      </TextMessage>
      <EmojiMessage v-else-if="message.type === 'emoji'" :data="message.data" />
      <FileMessage v-else-if="message.type === 'file'" :data="message.data" :messageColors="colors" :direction="message.direction" :class="messageClass" />
      <ImageMessage v-else-if="message.type === 'image'" :data="message.data" :class="messageClass" />
      <TypingMessage v-else-if="message.type === 'typing'" :indent="!!chatImageUrl" :messageColors="determineMessageColors()" :class="messageClass" />
      <SystemMessage v-else-if="message.type === 'system'" :data="message.data" :messageColors="determineMessageColors()">
          <slot name="system-message-body" :message="message.data">
          </slot>
      </SystemMessage>
      <CarouselMessage v-else-if="isCarouselMessage" :data="message.data" :messageColors="carouselMessageColors()" @buttonPressed="triggerButtonPressed" :class="messageClass" />
      <StructureMessage v-else-if="message.type === 'structure'" :data="message.data" :messageColors="determineMessageColorsStructure()" @buttonPressed="triggerButtonPressed" :class="messageClass" />
    </div>

    <Suggestions v-if="isLastMessage && message.suggestions" :suggestions="message.suggestions" v-on:sendSuggestion="triggerQuickReplyPressed" :colors="colors" />

    <div v-if="message.direction === 'sent' && message.error" class="sc-message-error">
      {{message.error}}
    </div>

    <Tooltip v-if="isAiGenerated" :text="$t('interface.aiGeneratedTooltip')">
      <div class="ai-generated-message-label">
        <img :src="AiGeneratedIcon" alt="AI Generated Icon" class="ai-generated-icon"/>
        <span>{{ $t('interface.aiGeneratedLabel') }}</span>
      </div>
    </Tooltip>
  </div>
</template>

<script>
import TextMessage from './messages/TextMessage.vue'
import FileMessage from './messages/FileMessage.vue'
import EmojiMessage from './messages/EmojiMessage.vue'
import TypingMessage from './messages/TypingMessage.vue'
import SystemMessage from './messages/SystemMessage.vue'
import CarouselMessage from './messages/CarouselMessage.vue'
import StructureMessage from './messages/StructureMessage.vue'
import ImageMessage from './messages/ImageMessage.vue'
import Suggestions from './Suggestions.vue'
import AiGeneratedIcon from '../../assets/ai-generated-icon.svg'
import Tooltip from './Tooltip.vue'
import { Event, EventBus } from '@/util/event-bus'

export default {
  components: {
    TextMessage,
    FileMessage,
    EmojiMessage,
    TypingMessage,
    SystemMessage,
    CarouselMessage,
    StructureMessage,
    ImageMessage,
    Suggestions,
    Tooltip
  },
  props: {
    message: {
      type: Object,
      required: true
    },
    colors: {
      type: Object,
      required: true
    },
    isLastMessage: {
      type: Boolean,
      default: false
    },
    isAiGenerated: {
      type: Boolean,
      default: false
    },
    onButtonPressed: {
      type: Function,
      required: true
    },
    messageStyling: {
      type: Boolean,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    messageLabel: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      AiGeneratedIcon
    }
  },

  methods: {
    carouselMessageColors () {
      return {
        '--button-text': this.colors.sentMessage.carouselButton.text,
        '--button-text-hover': this.colors.sentMessage.carouselButton.textHover,
        '--button-bg-hover': this.colors.sentMessage.carouselButton.bgHover
      }
    },
    sentColorsStyle (isStruct = false) {
      const styles = {
        color: this.colors.sentMessage.text,
        backgroundColor: isStruct ? this.colors.sentMessage.button.bg : this.colors.sentMessage.bg,
        fontSize: this.colors.messageBubble.fontSize,
        fontWeight: this.colors.messageBubble.fontWeight
      }
      if (isStruct) {
        styles.border = this.colors.sentMessage.button.border
        styles['--button-text'] = this.colors.sentMessage.button.text
        styles['--button-text-hover'] = this.colors.sentMessage.button.textHover
        styles['--button-bg-hover'] = this.colors.sentMessage.button.bgHover
        styles['--button-radius'] = this.colors.sentMessage.button.radius || '6px'
      } else {
        styles.lineHeight = this.colors.messageBubble.lineHeight
        styles.padding = this.colors.messageBubble.paddingText
      }
      return styles
    },
    receivedColorsStyle (isStruct = false) {
      return {
        color: this.colors.receivedMessage.text,
        marginRight: this.colors.receivedMessage.marginRight,
        backgroundColor: this.colors.receivedMessage.bg,
        fontSize: this.colors.messageBubble.fontSize,
        fontWeight: this.colors.messageBubble.fontWeight,
        lineHeight: this.colors.messageBubble.lineHeight,
        padding: isStruct ? this.colors.messageBubble.paddingStruct : this.colors.messageBubble.paddingText,
        opacity: this.colors.receivedMessage.opacity || '100%',
        backdropFilter: this.colors.receivedMessage.backdropFilter || null,
        boxShadow: this.colors.receivedMessage.boxShadow || null
      }
    },
    determineMessageColors () {
      return this.message.direction === 'sent' ? this.sentColorsStyle() : this.receivedColorsStyle()
    },
    determineMessageColorsStructure () {
      return {
        sent: this.sentColorsStyle(true),
        received: this.receivedColorsStyle(true)
      }
    },
    triggerButtonPressed (button) {
      EventBus.$emit(Event.INPUT_HIDE_VALIDATION)
      this.onButtonPressed({
        type: 'button',
        data: button
      })
    },
    triggerQuickReplyPressed (quickReply) {
      this.onButtonPressed({
        type: 'quickReply',
        data: quickReply
      })
    }
  },
  computed: {
    isCarouselMessage () {
      return this.message.type === 'structure' && Array.isArray(this.message.data)
    },
    authorName () {
      return this.user && this.user.name
    },
    chatImageUrl () {
      return (this.user && this.user.imageUrl) || this.chatIcon
    },
    messageClass () {
      return 'sc-message-bubble ' + (this.message.direction || '')
    },
    bubbleMetadata () {
      return this.message.metadata?.bubble || {}
    }
  }
}
</script>
